import AccordionSection from 'components/layout/accordion-section';
import {
  AirHandlerTypeCodeType,
  HyperHeatFilterValueType,
  SystemTypeFilterValueType
} from 'data/types';
import { ServerFilterOptionCountsType } from 'features/systems';
import { getAirHandlerTypeFilterLabel } from 'utils/air-handler-input-helpers';
import {
  HYPER_HEAT_LABELS,
  SYSTEM_TYPE_LABELS,
  SystemListFilterState,
  SystemListFilterUniverse,
  formatBtuBucket
} from './equipment-comparison-filters';
import { toggleSetValue } from './equipment-filter-helpers';
import FilterSection from './equipment-filter-section';
import { FilterOption } from './equipment-filter-section-types';

type EquipmentFiltersMenuProps = Readonly<{
  universe: SystemListFilterUniverse;
  state: SystemListFilterState;
  onChange: (next: SystemListFilterState) => void;
  condenserSeriesLabels: Record<string, string>;
  optionCounts: ServerFilterOptionCountsType;
  // Ducted path renders its own scoped 'System Type' section (AHA-6681 QA
  // F6); the leaf stays pathway-ignorant and just skips the legacy one.
  hideSystemTypeSection?: boolean;
}>;

type AccordionFilterSectionProps<T> = Readonly<{
  title: string;
  options: ReadonlyArray<FilterOption<T>>;
  enabled: Set<T>;
  onToggle: (value: T) => void;
}>;

function AccordionFilterSection<T>({
  title,
  options,
  enabled,
  onToggle
}: AccordionFilterSectionProps<T>) {
  if (options.length < 2) return null;
  const badge = enabled.size < options.length ? enabled.size : null;
  return (
    <AccordionSection title={title} badge={badge}>
      <FilterSection options={options} enabled={enabled} onToggle={onToggle} />
    </AccordionSection>
  );
}

export default function EquipmentFiltersMenu({
  universe,
  state,
  onChange,
  condenserSeriesLabels,
  optionCounts,
  hideSystemTypeSection = false
}: EquipmentFiltersMenuProps) {
  const airHandlerOptions: FilterOption<AirHandlerTypeCodeType>[] =
    universe.airHandlerTypes.map((code) => ({
      value: code,
      htmlId: `equipment-filter-airhandler-${code}`,
      label: getAirHandlerTypeFilterLabel(code),
      count: optionCounts.airHandlerTypes[code]
    }));

  const seriesOptions: FilterOption<string>[] = universe.condenserSeries.map(
    (series) => ({
      value: series,
      htmlId: `equipment-filter-series-${series.replace(/\s+/g, '-')}`,
      label: condenserSeriesLabels[series] ?? series,
      count: optionCounts.condenserSeries[series]
    })
  );

  const btuOptions: FilterOption<number>[] = universe.condenserBtus.map(
    (btu) => ({
      value: btu,
      htmlId: `equipment-filter-btu-${btu}`,
      label: formatBtuBucket(btu),
      count: optionCounts.condenserBtus[btu]
    })
  );

  const hyperHeatOptions: FilterOption<HyperHeatFilterValueType>[] =
    universe.hyperHeat.map((key) => ({
      value: key,
      htmlId: `equipment-filter-hyperheat-${key}`,
      label: HYPER_HEAT_LABELS[key],
      count: optionCounts.hyperHeat[key]
    }));

  const systemTypeOptions: FilterOption<SystemTypeFilterValueType>[] =
    universe.systemTypes.map((key) => ({
      value: key,
      htmlId: `equipment-filter-systemtype-${key}`,
      label: SYSTEM_TYPE_LABELS[key],
      count: optionCounts.systemTypes[key]
    }));

  // Refrigerant values are verbatim attribute strings and serve as their
  // own display labels.
  const refrigerantOptions: FilterOption<string>[] = universe.refrigerants.map(
    (refrigerant) => ({
      value: refrigerant,
      htmlId: `equipment-filter-refrigerant-${refrigerant.replace(
        /\s+/g,
        '-'
      )}`,
      label: refrigerant,
      count: optionCounts.refrigerants[refrigerant]
    })
  );

  return (
    <div className='flex flex-col'>
      <AccordionFilterSection
        title='Air Handler Type'
        options={airHandlerOptions}
        enabled={state.airHandlerTypes}
        onToggle={(type) =>
          onChange({
            ...state,
            airHandlerTypes: toggleSetValue(state.airHandlerTypes, type)
          })
        }
      />
      <AccordionFilterSection
        title='Condenser Series'
        options={seriesOptions}
        enabled={state.condenserSeries}
        onToggle={(series) =>
          onChange({
            ...state,
            condenserSeries: toggleSetValue(state.condenserSeries, series)
          })
        }
      />
      <AccordionFilterSection
        title='Condenser Size'
        options={btuOptions}
        enabled={state.condenserBtus}
        onToggle={(btu) =>
          onChange({
            ...state,
            condenserBtus: toggleSetValue(state.condenserBtus, btu)
          })
        }
      />
      <AccordionFilterSection
        title='Capability'
        options={hyperHeatOptions}
        enabled={state.hyperHeat}
        onToggle={(option) =>
          onChange({
            ...state,
            hyperHeat: toggleSetValue(state.hyperHeat, option)
          })
        }
      />
      {!hideSystemTypeSection && (
        <AccordionFilterSection
          title='System Type'
          options={systemTypeOptions}
          enabled={state.systemTypes}
          onToggle={(option) =>
            onChange({
              ...state,
              systemTypes: toggleSetValue(state.systemTypes, option)
            })
          }
        />
      )}
      <AccordionFilterSection
        title='Refrigerant'
        options={refrigerantOptions}
        enabled={state.refrigerants}
        onToggle={(refrigerant) =>
          onChange({
            ...state,
            refrigerants: toggleSetValue(state.refrigerants, refrigerant)
          })
        }
      />
    </div>
  );
}
